// contractRiskCells.tsx — geteilte Zell-Primitiven + Formatter für die
// Verträge- und Risiken-Redesign-Seiten (VertraegeTabelleRedesign /
// RisikenTabelleRedesign). Nutzen die `.kvp-rd`-Primitiven aus ./table und
// müssen daher innerhalb eines `.kvp-rd`-Wrappers gerendert werden.

import { Chip, Dash, TypeCell } from "./table";
import { FirmaIcon, PersonIcon } from "./kvpIcons";
import AuthedImage from "../common/AuthedImage";

/** EUR-Betrag (de-DE) oder leerer String, wenn nicht numerisch. */
export function formatEuro(value: unknown): string {
  const n = Number(value);
  return Number.isFinite(n)
    ? n.toLocaleString("de-DE", { style: "currency", currency: "EUR" })
    : "";
}

/** Datum (de-DE, tt.mm.jjjj) oder leerer String. */
export function formatDate(value: unknown): string {
  if (!value) return "";
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString("de-DE");
}

/**
 * Anzeigename eines Kontakts — defensiv. Verträge liefern `contact.displayName`
 * (Serializer), Risiken die rohe Entity (Getter nicht serialisiert) → Fallback
 * auf `lastname, firstName`.
 */
export function contactName(contact: any): string {
  if (!contact) return "";
  if (contact.displayName) return String(contact.displayName);
  const last = contact.lastname ?? "";
  const first = contact.firstName ?? contact.vorname ?? "";
  return [last, first].filter(Boolean).join(", ");
}

/** "Firma" vs. "Person" — `contact.type` ist Verträge=String, Risiken=Entity. */
function isFirma(contact: any): boolean {
  const t = typeof contact?.type === "string" ? contact.type : contact?.type?.type;
  return t === "Firma";
}

/**
 * Bezeichnung = Vertragsname aus den GDV-Daten (importiertes Merkmal
 * „Produkt"/„Produktname" in ContractCharacteristics), z. B. „OPTIMALPAKET",
 * „Dyn. Sach-Gebäudeversicherung", „Hausrat".
 *
 * Bewusst KEIN Fallback auf `insuranceProduct.name` — das ist die durch das
 * Sparten-Mapping gesetzte Vertragsart und steht bereits in der Vertragsart-
 * Spalte; als Bezeichnung würde es diese nur duplizieren. Fehlt der GDV-
 * Produktname (v. a. bei KFZ-Verträgen), bleibt die Bezeichnung leer.
 */
export function produktBezeichnung(contract: any): string {
  const chars = contract?.characteristics;
  if (Array.isArray(chars)) {
    const p = chars.find((c: any) =>
      ["produkt", "produktname"].includes(String(c?.nameBezeichnung ?? "").toLowerCase()),
    );
    if (p?.contractCharacteristicValue) return String(p.contractCharacteristicValue);
  }
  return "";
}

/**
 * Menschenlesbares Label eines Risikoobjekts. `name` trägt das Kennzeichen
 * (KFZ, z. B. „AC-MA 2806 · Opel") bzw. die Anschrift (Immobilie, z. B.
 * „Nizzaallee 52, Aachen"); `riskObjectIdentifier` ist nur der technische Code
 * (`KFZ-…`/`IMMO-…`) → daher `name` zuerst.
 */
export function riskObjectDisplay(ro: any): string {
  return ro?.name || ro?.riskObjectIdentifier || ro?.riskType || "";
}

/**
 * Risikoobjekt-Label eines Vertrags: die verknüpften Risikoobjekte als
 * Kennzeichen/Anschrift, mehrere kommagetrennt.
 */
export function risikoobjektLabel(contract: any): string {
  const ros = contract?.riskObjects;
  if (!Array.isArray(ros) || ros.length === 0) return "";
  const labels = ros.map(riskObjectDisplay).filter(Boolean);
  return Array.from(new Set(labels)).join(", ");
}

/** Kontakt-Zelle: Firma/Person-Icon + Name. */
export function KontaktPill({ contact }: { contact: any }) {
  const name = contactName(contact);
  if (!name) return <Dash />;
  return <TypeCell icon={isFirma(contact) ? FirmaIcon : PersonIcon} label={name} />;
}

/**
 * Verantwortlichkeit des Kunden (FO/BO). BO = Team, FO = Person; beide als
 * kompakte Chips mit Präfix. Fehlende Seite entfällt.
 */
export function VerantwortlichkeitCell({ contact }: { contact: any }) {
  const bo = contact?.accountManagerBO?.name?.trim();
  const fo = contact?.accountManagerFO?.name?.trim();
  if (!bo && !fo) return <Dash />;
  return (
    <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
      {bo ? <Chip tone="chip-bo">{"BO: " + bo.replace(/^Team\s+/i, "")}</Chip> : null}
      {fo ? <Chip tone="chip-fo">{"FO: " + fo}</Chip> : null}
    </span>
  );
}

function initialsOf(name: string): string {
  const clean = name.replace(/[^A-Za-zÄÖÜäöü0-9 ]/g, " ").trim();
  if (!clean) return "?";
  return clean
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

/**
 * Gesellschaft als Logo-Bild (authentifiziert via AuthedImage) + Name. Solange
 * kein Logo hochgeladen ist (404) fällt AuthedImage auf ein Monogramm zurück.
 */
export function GesellschaftLogo({
  id,
  name,
}: {
  id?: number | null;
  name?: string | null;
}) {
  const n = (name ?? "").trim();
  if (!id && !n) return <Dash />;
  const mono = (
    <span
      aria-hidden
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        borderRadius: 4,
        background: "#efe7ed",
        color: "#7a5c72",
        fontSize: 10,
        fontWeight: 700,
        flex: "0 0 auto",
      }}
    >
      {initialsOf(n)}
    </span>
  );
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }} title={n}>
      {id ? (
        <AuthedImage
          src={`gesellschaften/${id}/logo`}
          alt={n}
          fallback={mono}
          style={{ height: 22, maxWidth: 64, objectFit: "contain", borderRadius: 4, flex: "0 0 auto" }}
        />
      ) : (
        mono
      )}
      <span
        style={{
          // Feste Max-Breite: nur so greift text-overflow:ellipsis in der
          // auto-layout-Tabelle (sonst wächst die Zelle mit dem Namen und läuft
          // in die Nachbarspalte). Logo zeigt die Identität, voller Name im title.
          maxWidth: 120,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {n || <Dash />}
      </span>
    </span>
  );
}
